import * as d3 from "d3-selection";
import {json} from "d3-fetch";
import {transition} from "d3-transition";
import dayjs from "dayjs/esm";
import MicroModal from 'micromodal';
import {getAsync} from "./utils";
import {layout, timeline, layers, map, data, logPageView} from "./common";
import {
  config,
  webcamsRefs,
  harborsRefs,
  sensorsRefs,
  tempSensorsRefs,
  windColor,
  windColorsGradient,
  loadWebcams,
  loadHarbors,
  waveColor,
  singleColorGradient,
  waveSensorsRefs,
  tempColor, piafRainColor, waterTempSensorsRefs
} from "./config";
import {
  projectInMap,
  displayCoastLine,
  displayMapRef, clearBgLayers, toggleMap
} from "./map";
import {appendToTimeline, defaultActiveStep, renderTimeline, updateTimelineDatetime} from "./timeline";
import {computeNebIcon, weatherDetails} from "./meteogramme";
import navIcons from "url:../img/nav/*.svg";
import weatherIcons from "url:../img/weather/*.svg";
import {ImageOverlay} from "leaflet/src/layer/ImageOverlay";
import router from "./router";
import * as L from "leaflet/dist/leaflet";
import GeoRasterLayer from "georaster-layer-for-leaflet/dist/georaster-layer-for-leaflet";
import geoblaze from 'geoblaze';
import {sensorDetails} from "./sensor";

export function refreshWindSensorsData(timelineOnly = false) {
  console.log('refreshWindSensorsData');
  getAsync("https://private2.windmorbihan.com/mesures/getlastalljson.json", function (dt) {
    if (layers.dataLayers.indexOf('wind') !== -1 && !timelineOnly) {
      renderWindSensors(dt);
    }
    if (timeline.timelineSteps) {
      appendToTimeline(dt);
      renderTimeline(true);
    }
  });
}

function loadWindSensorsData(latest = false) {
  if (latest) {
    getAsync("https://private2.windmorbihan.com/mesures/getlastalljson.json", function (dt) {
      renderWindSensors(dt);
    });
  } else {
    const currentTs = parseInt(timeline.timelineActiveStep.getAttribute('data-ts'));
    renderWindSensors(Object.values(data.timelineData[currentTs] || {}));
  }
}

export function refreshWaveSensorsData() {
  getAsync("https://private2.windmorbihan.com/mesures/latest-waves.json", function (dt) {
    if (layers.dataLayers.indexOf('waves') !== -1) {
      renderWaveSensors(dt);
    }
  });
}

function loadWaveSensorsData(latest = false) {
  if (latest) {
    getAsync("https://private2.windmorbihan.com/mesures/latest-waves.json", function (dt) {
      renderWaveSensors(dt);
    });
  } else {
    const currentTs = parseInt(timeline.timelineActiveStep.getAttribute('data-ts'));
    if (layers.dataLayers.indexOf('waves') !== -1) {
      renderWaveSensors(Object.values(data.timelineData[currentTs] || {}));
    }
  }
}

export function refreshTempSensorsData() {
  getAsync("https://private2.windmorbihan.com/mesures/latest-temps.json", function (dt) {
    if (layers.dataLayers.indexOf('temp') !== -1) {
      renderTempSensors(dt);
    }
  });
}

function loadTempSensorsData(latest = false) {
  if (latest) {
    getAsync("https://private2.windmorbihan.com/mesures/latest-temps.json", function (dt) {
      renderTempSensors(dt);
    });
  } else {
    const currentTs = parseInt(timeline.timelineActiveStep.getAttribute('data-ts'));
    renderTempSensors(Object.values(data.timelineData[currentTs] || {}));
  }
}

export function displayWindForecasts(timestamp) {
  let emptyForecastData = false;

  if (map.activeMap === 'wm') {
    clearBgLayers();
  }

  return fetch(config.backEndUrl + "/live_forecasts/raster.json?metric=speed" + (timestamp ? ("&timestamp=" + timestamp) : ""))
    .then(function (resp) {
      return resp.json();
    })
    .then(function (rasterData) {
      if (rasterData.width) {
        renderForecastInfo(rasterData.source, rasterData.grid, rasterData.run_timestamp);
        if (map.activeMap === 'wm' ||  map.activeMap === 'elevation') {
          return displayCoastLine(layers.dataLayers.indexOf('waves_fc') !== -1).then(function () {
            if (layers.forecastsLayer && layers.forecastsLayer.overlay && (layers.dataLayers.indexOf('waves_fc') === -1 && layers.dataLayers.indexOf('wind_fc') !== -1)) {
              map.instance.removeLayer(layers.forecastsLayer.overlay);
            }

            if (layers.dataLayers.indexOf('waves_fc') === -1 && layers.dataLayers.indexOf('wind_fc') !== -1) {
              // Note : bounds adjusted to compensate img distortion
              layers.forecastsLayer = {
                forecastId: rasterData.id,
                forecastTimestamp: timestamp,
                overlay :  new ImageOverlay(rasterData.raster, config.forecastsBounds,
                  {className: 'fc_colors', pane: 'tilePane'}).addTo(map.instance)
              };
            }
            document.querySelector("#map_container").classList.add('rendered');
          });
        } else if (map.activeMap === 'satellite') {
          if (layers.forecastsLayer && layers.forecastsLayer.overlay && (layers.dataLayers.indexOf('waves_fc') === -1 && layers.dataLayers.indexOf('wind_fc') !== -1)) {
            map.instance.removeLayer(layers.forecastsLayer.overlay);
          }
            layers.forecastsLayer = {
              forecastId: rasterData.id,
              forecastTimestamp: timestamp
            };
            document.querySelector("#map_container").classList.add('rendered');
        } else {
          return Promise.resolve();
        }
      } else {
        emptyForecastData = true;
        renderForecastInfo(null);
        if (layers.forecastsLayer && layers.forecastsLayer.overlay) {
          map.instance.removeLayer(layers.forecastsLayer.overlay);
        }
        document.querySelector("#map_container").classList.remove('rendered');
        return displayMapRef(map.activeMap, emptyForecastData);
      }
    }).then(function () {
      if (!emptyForecastData) {
        initWindParticles();
        return fetch(config.backEndUrl + "/live_forecasts/raster.json?metric=direction" + (timestamp ? ("&timestamp=" + timestamp) : ""))
          .then(function (resp) {
            return resp.json();
          })
          .then(function (rasterData) {
            if (rasterData.width) {
              map.rasterWidth = rasterData.width;
              map.rasterHeight = rasterData.height;
              map.windLayer.start();
              if (map.activeMap === 'satellite') {
                map.windLayer.loadColorGradient(windColorsGradient(rasterData));
              } else {
                map.windLayer.loadColorGradient(singleColorGradient('#FFFFFF'));
              }
              map.windLayer.setWindData({
                uMin: rasterData.u_min,
                uMax: rasterData.u_max,
                vMin: rasterData.v_min,
                vMax: rasterData.v_max,

                width: rasterData.width,
                height: rasterData.height,

                image: rasterData.path
              });
              updateParticlesBBox();
            }
          });
      }
    });
}

// Note - WindMap expects the bottomLeft rect corner relative position from the bottomLeft raster corner
export function updateParticlesBBox() {
  console.debug('updateParticlesBBox');
  try {

    let scale = map.instance.getZoomScale(map.instance.getZoom(), map.refZoom),
      bottomLeft = map.instance.project(map.instance.getBounds().getSouthWest(), map.refZoom);
    // console.log('scale : ' + scale + ' - bottomLeft : ' + bottomLeft);
    let mSize = map.instance.getSize();
    let nWidth = (mSize.x / map.windWidth) / scale;
    let nHeight = (mSize.y / map.windHeight) / scale;
    let x, y, rect;
    // console.log('relative rect : ' + JSON.stringify(rect));
    // console.log('actual rect : ' + JSON.stringify({
    //   width: Math.round(rect.width * map.rasterWidth), height: Math.round(rect.height * map.rasterHeight),
    //   x: Math.round(rect.x * map.rasterWidth), y: Math.round(rect.y * map.rasterHeight)
    // }));
    if (map.windLayer && layers.dataLayers.indexOf('wind_fc') !== -1) {
      x = Math.abs(bottomLeft.x - map.windBox[0].x) / map.windWidth;
      y = Math.abs(bottomLeft.y - map.windBox[1].y) / map.windHeight;
      rect = {width: nWidth, height: nHeight, x: x, y: y};
      map.windLayer.setWindMapRect(rect.x, rect.y, rect.width, rect.height);
      map.windLayer.start();
    }
    if (map.wavesLayer && layers.dataLayers.indexOf('waves_fc') !== -1) {
      x = Math.abs(bottomLeft.x - map.wavesBox[0].x) / map.wavesWidth;
      y = Math.abs(bottomLeft.y - map.wavesBox[1].y) / map.wavesHeight;
      rect = {width: nWidth, height: nHeight, x: x, y: y};
      map.wavesLayer.setWindMapRect(rect.x, rect.y, rect.width, rect.height);
      map.wavesLayer.start();
    }
  } catch (e) {
    console.error('updateParticlesBBox error: ' + e);
  }
}

function onWebGLFailure() {
  console.error("WebGL Context lost");
}

function initWindParticles() {
  if (!map.windLayer) {
    let params = {
      container: document.getElementById("particles_wrapper"),
      webGLFailureCallback: onWebGLFailure
    };
    map.windLayer = new WindMap(params);
    map.windLayer.opacity = 1;
    map.windLayer.fadeOut = 0.88;
    map.windLayer.thickness = 1.5;
    map.windLayer.speedFactor = 1.5;
    map.windLayer.particlesNumber = 650;
    map.windLayer.dropRate = 0.05;
  }
}

function initWavesParticles() {
  if (!map.wavesLayer) {
    let params = {
      container: document.getElementById("waves_wrapper"),
      webGLFailureCallback: onWebGLFailure
    };
    map.wavesLayer = new WindMap(params);
    map.wavesLayer.opacity = 0.8;
    map.wavesLayer.fadeOut = 0.999;
    map.wavesLayer.thickness = 2;
    map.wavesLayer.speedFactor = 0.5;
    map.wavesLayer.particlesNumber = 2000;
    map.wavesLayer.dropRate = 0.2;
  }
}

function renderForecastInfo(source, grid, runTs) {
  let dataInfo = document.querySelector("#data_info"), fcInfo = dataInfo.querySelector("#fc_info");
  if (source) {
    let sourceDetail = "", runTimestamp = /^\d+$/.test(runTs) ? dayjs.unix(runTs) : dayjs(runTs);
    let runTsLabel = runTimestamp.format("DD/MM à H[h]");
    if (source === "arome" || source === 'arpege') {
      sourceDetail = "Météo-France (" + source.toUpperCase() + " " + grid + ")";
    } else if (source === 'piaf') {
      sourceDetail = "Météo-France (PIAF)";
      runTsLabel = runTimestamp.format("DD/MM à H[h]mm");
    } else {
      sourceDetail = source.toUpperCase();
    }
    if (sourceDetail === "WW3") {
      sourceDetail = "WW3 Météo-France (haute résolution)";
    }
    let fcContent = '<p>Prévis. du ' + runTsLabel + '<br/><em>' + sourceDetail + '</em></p>';
    if (fcInfo) {
      fcInfo.innerHTML = fcContent;
    } else {
      console.log('sourceDetail: ' + fcContent);
      dataInfo.insertAdjacentHTML("beforeend", '<div id="fc_info">' + fcContent + '</div>');
    }
  } else {
    let emptyContent = "<p>Données indisponibles</p>";
    if (fcInfo) {
      fcInfo.innerHTML = emptyContent;
    } else {
      dataInfo.insertAdjacentHTML("beforeend", emptyContent);
    }
  }
}

function renderWindInfo() {
  let dataInfo = document.querySelector("#data_info"), windInfo = dataInfo.querySelector("#wind_info"),
    windContent = '<p>Vents réels (et rafales) en nœuds</p>';
  if (!windInfo) {
    dataInfo.insertAdjacentHTML("beforeend", '<div id="wind_info">' + windContent + '</div>');
  }
}

function renderAmpInfo() {
  let dataInfo = document.querySelector("#data_info"), ampInfo = dataInfo.querySelector("#amp_info"),
    ampContent = '<p>Données Office français de la biodiversité</p>';
  if (!ampInfo) {
    dataInfo.insertAdjacentHTML("beforeend", '<div id="amp_info">' + ampContent + '</div>');
  }
}

export function hideWindForecasts() {
  if (layers.forecastsLayer && layers.forecastsLayer.overlay) {
    map.instance.removeLayer(layers.forecastsLayer.overlay);
  }
  layers.forecastsLayer = null;
  if (map.windLayer) {
    map.windLayer.stop();
  }
  if (document.querySelector("#fc_info")) {
    document.querySelector("#fc_info").innerHTML = "";
  }
}

export function displayWavesForecasts(timestamp) {
  let emptyForecastData = false;
  if (map.activeMap === 'wm') {
    clearBgLayers();
  }
  return fetch(config.backEndUrl + "/live_forecasts/raster.json?source=ww3&metric=wave" + (timestamp ? ("&timestamp=" + timestamp) : ""))
    .then(function (resp) {
      if (resp.ok) {
        return resp.json();
      } else {
        throw new Error("Forecast data not found");
      }
    })
    .then(function (rasterData) {
      // console.log("rasterData: " + JSON.stringify(rasterData));
      if (rasterData.width) {
        renderForecastInfo(rasterData.source, rasterData.grid, rasterData.run_timestamp);
        return displayCoastLine(true, true).then(function () {
          if (layers.forecastsLayer && layers.forecastsLayer.overlay) {
            map.instance.removeLayer(layers.forecastsLayer.overlay);
          }
          // Note : bounds adjusted to compensate img distortion
          // Improvement : reproject properly to avoid distortion in the whole area
          layers.forecastsLayer = {
            forecastId: rasterData.id,
            forecastTimestamp: timestamp,
            overlay: new ImageOverlay(rasterData.raster, config.forecastsBounds,
              {className: 'fc_colors', pane: 'tilePane'}).addTo(map.instance)
          };
          document.querySelector("#map_container").classList.add('rendered');
        });
      } else {
        emptyForecastData = true;
        renderForecastInfo(null);
        if (layers.forecastsLayer && layers.forecastsLayer.overlay) {
          map.instance.removeLayer(layers.forecastsLayer.overlay);
        }
        document.querySelector("#map_container").classList.remove('rendered');
        return displayMapRef(map.activeMap);
      }
    }).then(function () {
      if (!emptyForecastData) {
        initWavesParticles();
        return fetch(config.backEndUrl + "/live_forecasts/raster.json?metric=wave_direction&source=ww3" + (timestamp ? ("&timestamp=" + timestamp) : ""))
          .then(function (resp) {
            return resp.json();
          })
          .then(function (rasterData) {
            if (rasterData.width) {
              map.rasterWidth = rasterData.width;
              map.rasterHeight = rasterData.height;
              map.wavesLayer.start();
              map.wavesLayer.loadColorGradient(singleColorGradient('#02ACF1'));
              map.wavesLayer.setWindData({
                uMin: rasterData.u_min,
                uMax: rasterData.u_max,
                vMin: rasterData.v_min,
                vMax: rasterData.v_max,

                width: rasterData.width,
                height: rasterData.height,

                image: rasterData.path
              });
              updateParticlesBBox();
            }
          });
      }
    }).catch(function(err) {
      console.warn("Could not retrieve waves forecast data for provided timestamp");
      hideWavesForecasts();
      displayMapRef(map.activeMap, true);
    });
}

export function hideWavesForecasts() {
  console.log('hideWavesForecasts');
  if (layers.forecastsLayer) {
    map.instance.removeLayer(layers.forecastsLayer.overlay);
  }
  layers.forecastsLayer = null;
  if (map.wavesLayer) {
    map.wavesLayer.stop();
  }
  if (document.querySelector("#fc_info")) {
    document.querySelector("#fc_info").innerHTML = "";
  }
}

export function initMeasuresLayer() {
  layers.overlay.node().innerHTML =
    '<symbol id="arrow" viewBox="0 0 43.4 47.4" width="24px" height="26px">' +
    '<path stroke-width="2" d="M3.3,3.6C3.7,3.2,4.4,3,5.1,3.2l16.7,5.4l16.6-5.5c0.7-0.2,1.4,0,1.8,0.5c0.5,0.5,0.6,1.3,0.3,1.9L23.5,43.4c-0.2,0.6-0.9,1-1.6,1c-0.7,0-1.3-0.4-1.6-1L2.9,5.6C2.7,5,2.8,4.2,3.3,3.6C3.2,3.7,3.2,3.7,3.3,3.6z"/>' +
    '</symbol>' +
    '<symbol id="alt_arrow" width="22px" height="24px" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<g id="group" transform="rotate(180) translate(-23, -24)">' +
    '<g id="Subtract">' +
    '<mask id="path-1-outside-1_31_8077" maskUnits="userSpaceOnUse" x="1.5" y="0" width="21" height="23" fill="black">' +
    '<rect fill="white" x="1.5" width="21" height="23"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M20.3476 21.9103C20.6989 22.0119 21.0501 21.9103 21.2508 21.7073C21.5017 21.4028 21.5519 20.9968 21.4515 20.6923L18.7075 14.6627L11.9791 12.0841L5.25738 14.7161L2.58468 20.743C2.43414 21.0475 2.48432 21.4536 2.73521 21.7073C2.93592 21.9611 3.28716 22.0626 3.63841 21.9611L11.9679 19.1697L20.3476 21.9103ZM6.41003 12.1169L11.6106 10.0806L11.9713 9.93931L12.333 10.0779L17.5268 12.0684L12.7206 1.50753C12.5701 1.20301 12.269 1 11.9177 1C11.5665 1 11.2153 1.20301 11.1149 1.50753L6.41003 12.1169Z"/>' +
    '</mask>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M20.3476 21.9103C20.6989 22.0119 21.0501 21.9103 21.2508 21.7073C21.5017 21.4028 21.5519 20.9968 21.4515 20.6923L18.7075 14.6627L11.9791 12.0841L5.25738 14.7161L2.58468 20.743C2.43414 21.0475 2.48432 21.4536 2.73521 21.7073C2.93592 21.9611 3.28716 22.0626 3.63841 21.9611L11.9679 19.1697L20.3476 21.9103ZM6.41003 12.1169L11.6106 10.0806L11.9713 9.93931L12.333 10.0779L17.5268 12.0684L12.7206 1.50753C12.5701 1.20301 12.269 1 11.9177 1C11.5665 1 11.2153 1.20301 11.1149 1.50753L6.41003 12.1169Z" fill="white"/>' +
    '</g>' +
    '</g>' +
    '</symbol>' +
    '<symbol id="wave_arrow" viewBox="0 0 100 100" width="24px" height="26px">' +
    '<g transform="translate(0,-952.36218)">' +
    '<path d="m 49.999995,1037.3622 27.00001,-38.00002 -17,3.00002 4,-35.00002 -28.00001,0 4,35.00002 -17,-3.00002 z" stroke-width="2"/>' +
    '</g>' +
    '</symbol>' +
    '<symbol id="wave_sensor_icon" viewBox="0 0 576 512" width="24px" height="26px">' +
    '<path d="M0 336c0-17.7 14.3-32 32-32c35.3 0 64-28.7 64-64C96 125.1 189.1 32 304 32c84.7 0 157.6 50.7 190 123.2c4.7 10.5 1.3 22.8-8.1 29.4s-22.2 5.6-30.4-2.3C441 168.5 421.5 160 400 160c-44.2 0-80 35.8-80 80c0 35.3 28.7 64 64 64c0 0 0 0 0 0c26.9 0 55.4-10.8 77.4-26.1c0 0 0 0 0 0c11.9-8.5 28.1-7.8 39.2 1.7c14.4 11.9 32.5 21 50.6 25.2c15 3.5 25.1 17.1 24.7 31.9l0 41.9c-28.7 0-55.1-8.9-73.8-17.4c-5.5-2.5-10.3-5-14.5-7.2c-4.8-2.6-10.5-2.6-15.3 0c-4.2 2.3-9.1 4.8-14.5 7.2c-18.7 8.5-45.1 17.4-73.8 17.4s-55.1-8.9-73.8-17.4c-5.5-2.5-10.3-5-14.5-7.2c-4.8-2.6-10.5-2.6-15.3 0c-4.2 2.3-9.1 4.8-14.5 7.2c-18.7 8.5-45.1 17.4-73.8 17.4s-55.1-8.9-73.8-17.4c-5.5-2.5-10.3-5-14.5-7.2c-4.8-2.6-10.5-2.6-15.3 0c-4.2 2.3-9.1 4.8-14.5 7.2C55.1 369.7 28.7 378.6 0 378.6L0 336zm0 74.6c35 0 66.1-10.8 87.1-20.3c3.2-1.4 6.1-2.9 8.9-4.3c2.8 1.4 5.8 2.8 8.9 4.3c20.9 9.5 52 20.3 87.1 20.3s66.1-10.8 87.1-20.3c3.2-1.4 6.1-2.9 8.9-4.3c2.8 1.4 5.8 2.8 8.9 4.3c20.9 9.5 52 20.3 87.1 20.3s66.1-10.8 87.1-20.3c3.2-1.4 6.1-2.9 8.9-4.3c2.8 1.4 5.8 2.8 8.9 4.3c21 9.5 52 20.3 87.1 20.3l0 36.6c.1 2.7-.2 5.4-.8 8.1c-4 17.2-21.2 27.9-38.4 23.9c-24.5-5.7-44.9-16.5-58.2-25C449.5 469.7 417 480 384 480c-31.9 0-60.6-9.9-80.4-18.9c-5.8-2.7-11.1-5.3-15.6-7.7c-4.5 2.4-9.7 5.1-15.6 7.7c-19.8 9-48.5 18.9-80.4 18.9c-33 0-65.5-10.3-94.5-25.8c-13.4 8.4-33.7 19.3-58.2 25c-17.2 4-34.4-6.7-38.4-23.9c-.6-2.4-.8-4.9-.8-7.3c0 0 0 0 0 0s0 0 0 0l0-37.4z"/>' +
    '</symbol>' +
    '<g class="zoom_wrapper"></g>';
}

export function initForecastsLayer() {
  layers.forecastsLayer.node().innerHTML =
    '<symbol id="wave_arrow" viewBox="0 0 100 100" width="16px">' +
    '<g transform="translate(0,-952.36218)">' +
    '<path d="m 49.999995,1037.3622 27.00001,-38.00002 -17,3.00002 4,-35.00002 -28.00001,0 4,35.00002 -17,-3.00002 z" fill="none" stroke="#0c4da2"/>' +
    '</g>' +
    '</symbol>' +
    '<g class="zoom_wrapper"></g>';
}

export function initSensorsPolling() {
  if (!data.sensorsPolling) {
    data.sensorsPolling = setInterval(() => {
      if (!timeline.timelineActiveStep || timeline.timelineActiveStep === defaultActiveStep()) {
        refreshWindSensorsData();
        if (layers.dataLayers.indexOf('waves') !== -1) {
          refreshWaveSensorsData();
        }
        if (layers.dataLayers.indexOf('temp') !== -1) {
          refreshTempSensorsData();
        }
      }
    }, 30000);
  }
}

export function displayWindMeasures() {
  const latest = (!timeline.timelineActiveStep || timeline.timelineActiveStep === defaultActiveStep());
  loadWindSensorsData(latest);
}

export function displayWaveMeasures() {
  const latest = (!timeline.timelineActiveStep || timeline.timelineActiveStep === defaultActiveStep());
  loadWaveSensorsData(latest);
}

export function hideWindMeasures() {
  layers.overlay.selectAll(".wind_sensor").remove();
}

export function hideWaveMeasures() {
  layers.overlay.selectAll(".wave_sensor").remove();
}

export function displayPOIs(timestamp) {
  const spotPois = layers.activeSpot?.pois || [];
  console.debug('activeSpot: ' + layers.activeSpot?.ref + ' - spotPois: ' + JSON.stringify(spotPois));

  getAsync(config.backEndUrl + "/live_forecasts/weather.json" + (timestamp ? ("?timestamp=" + timestamp) : ""), function (poisData) {
    layers.overlay.select(".zoom_wrapper").selectAll(".weather_poi").remove();
    const activePois = poisData.pois.filter((d) =>
      !spotPois.length || spotPois.indexOf(d.slug) !== -1
    );
    const poisTs = parseInt(poisData.timestamp);
    let pois = layers.overlay.select(".zoom_wrapper").selectAll(".weather_poi")
      .data(activePois, function (d) {
        return d.name;
      }).join("g")
      .attr("class", function (d) {
        return `weather_poi ${d.slug === layers.activePoi?.slug ? 'poi-active' : ''}`;
      })
      .attr("id", function (d) {
        return "poi_" + d.slug;
      })
      .attr("transform", function (d) {
        return "translate(" + projectInMap(d.lng, d.lat) + ")";
      });

    if (layers.activeSpot || layout.edit) {
      pois.on("click", weatherDetails);
    }

    renderForecastInfo(activePois[0].source, activePois[0].grid, activePois[0].run_ts);
    pois.append("foreignObject").attr("width", 80).attr("height", 64).attr("transform", "translate(-40, -36)")
      .append("xhtml:body")
      .html(function (d, j) {
        let poiData = Object.values(activePois)[j];
        if (poiData) {
          let currentRain = (poiData.tw || poiData.tp || 0), prevRain = (poiData.prev_tw || poiData.prev_tp || 0);
          let icon = computeNebIcon(poiData.hc, poiData.mc, poiData.lc, poisTs, currentRain - prevRain);
          if (layers.activeSpot || layout.edit) {
            return "<div class='poi_icon spot_poi'>" +
                "<img src='" + weatherIcons[icon] + "' alt='" + d.name + "'/>" +
                "<span style='color: " + tempColor(poiData.temp) + ";'>" + poiData.temp + "°</span>" +
                "<img class='active_icon' src='" + weatherIcons.active_poi_icon + "' alt='" + d.name + "'/>" +
              "</div>" +
              "<p class='poi_name'>" + d.name + "</p>";
          } else {
            return "<a href='/previsions/" + d.slug + "' class='poi_icon' data-navigo><img src='" + weatherIcons[icon] + "' alt='" + d.name + "'/><span style='color: " + tempColor(poiData.temp) + ";'>" + poiData.temp + "°</span><img class='active_icon' src='" + weatherIcons.active_poi_icon + "' alt='" + d.name + "'/></a>" +
              "<p class='poi_name'>" + d.name + "</p>";
          }
        }
      });
    router.updatePageLinks();
  });
}

export function hidePOIs() {
  layers.overlay.selectAll(".weather_poi").remove();
}

export function displayWaterTemp() {
  if (!timeline.timelineActiveStep || timeline.timelineActiveStep === defaultActiveStep()) {
    getAsync("https://private2.windmorbihan.com/mesures/getmapelement-water_temp.html", function (html) {
      let parser = new DOMParser(), tempDoc = parser.parseFromString(html, "text/html");

      layers.overlay.select(".zoom_wrapper").selectAll(".water_temp_sensor").data(waterTempSensorsRefs, function (d) {
        return d.nid;
      }).join(
        function (enter) {
          let waterTempSensors = enter.append("g").attr("class", "water_temp_sensor").attr("id", function (d) {return "sensor_" + d.nid;})
            .attr("transform", function (d) {
              return "translate(" + projectInMap(d.lng, d.lat) + ")";
            })
            .append("g").attr("class", "water_temp_details").attr("transform", "translate(64, 18)")
            .append("foreignObject").attr("width", 30).attr("height", 30)
            .append("xhtml:body")
            .html(function (d) {
              return "<p>" + tempDoc.querySelector('[data-nid="' + d.nid + '"]').textContent.replace(/\.\d+/, '').replace(/\s+C/, '') + "</p>";
            });
          waterTempSensors.on("click", function(evt, d) {
            evt.stopPropagation();
            router.navigate("/" + waterTempSensorsRefs[0].slug);
          });
          waterTempSensors.on("mouseover", function (evt, d) {
            d3.select(this).raise();
            d3.select("#sensor_" + d.nid).append("text").attr("class", "sensor_name").text(waterTempSensorsRefs[0].label);
          }).on("mouseout", function (evt, d) {
            d3.selectAll(".sensor_name").remove();
            d3.select(this).lower();
          });
          return waterTempSensors;
        },
        function (update) {
          update.select("g foreignObject body p").text(function(d) {
            return tempDoc.querySelector('[data-nid="' + d.nid + '"]').textContent.replace(/\.\d+/, '').replace(/\s+C/, '');
          });
        }
      );
    }, true);
  } else {
    hideWaterTemp();
  }
}

export function hideWaterTemp() {
  layers.overlay.selectAll(".water_temp_sensor").remove();
}

export function displayWebcams() {
  const spotWebcams = layers.activeSpot?.webcams || [];
  console.debug('activeSpot: ' + layers.activeSpot?.ref + ' - spotWebcams: ' + JSON.stringify(spotWebcams));

  loadWebcams(function() {
    let activeWebcams = webcamsRefs.filter((d) =>
        !spotWebcams.length || spotWebcams.indexOf(`/webcam/${d.slug}`) !== -1
    );
    let webcams = layers.overlay.select(".zoom_wrapper").selectAll(".webcam_spot").data(activeWebcams).join("g").attr("class", "webcam_spot")
      .attr("transform", function (d) {
        return "translate(" + projectInMap(d.lng, d.lat) + ")";
      });
    if (layers.activeSpot) {
      webcams.on("click", webcamDetails);
    }

    webcams.append("foreignObject").attr("width", 32).attr("height", 32).attr("transform", "translate(-16, -16)")
      .append("xhtml:body")
      .html(function (d) {
        const webcamIcon = `<img src="${navIcons['webcam-map']}" alt="${d.label}">`;
        if (layers.activeSpot) {
          return `<div>${webcamIcon}</div>`;
        } else {
          return `<a href="/webcam/${d.slug}" title="${d.label}" data-navigo>${webcamIcon}</a>`;
        }
      });
    router.updatePageLinks();
  });
}

export function hideWebcams() {
  layers.overlay.selectAll(".webcam_spot").remove();
}

export function displayTemperatures() {
  const latest = (!timeline.timelineActiveStep || timeline.timelineActiveStep === defaultActiveStep());
  loadTempSensorsData(latest);
}

export function hideTemperatures() {
  layers.overlay.selectAll(".temp_sensor .temp_details").remove();
}

export function hideWaterTemperatures() {
  layers.overlay.selectAll(".water_temp_sensor").remove();
}

export function displayTide() {
  const spotTideGauges = layers.activeSpot?.tideGauges || [];
  console.debug('activeSpot: ' + layers.activeSpot?.ref + ' - spotTideGauges: ' + JSON.stringify(spotTideGauges));

  loadHarbors(function() {
    const activeTideGauges = harborsRefs.filter((d) =>
      !spotTideGauges.length || spotTideGauges.indexOf(`/port/${d.slug}`) !== -1
    );
    let harbors = layers.overlay.select(".zoom_wrapper").selectAll(".tide_spot")
      .data(activeTideGauges).join("g").attr("class", "tide_spot")
      .attr("transform", function (d) {
        return "translate(" + projectInMap(d.lng, d.lat) + ")";
      });

    if (layers.activeSpot) {
      harbors.on("click", tideDetails);
    }

    harbors.append("foreignObject").attr("width", 32).attr("height", 32).attr("transform", "translate(-16, -16)")
      .append("xhtml:body")
      .html(function (d) {
        const tideIcon = `<img src="${navIcons['tide-map']}" alt="${d.label}">`;
        if (layers.activeSpot) {
          return `<div>${tideIcon}</div>`;
        } else {
          return `<a href="/port/${d.slug}" title="${d.label}" data-navigo>${tideIcon}</a>`;
        }
      });
    router.updatePageLinks();
  });
}

export function hideTide() {
  layers.overlay.selectAll(".tide_spot").remove();
}

export function displayPiafLayer(timestamp) {
  let refTs, refTime;
  if (timestamp) {
    refTs = new Date(timestamp * 1000);
  } else {
    const now = new Date().getTime();
    refTs = new Date(now - (now % 300000));
  }
  refTime = refTs.toISOString();
  const refDate = refTime.split('T')[0];
  refTime = refTime.replace(/[-:]/g, '').replace('T', '_').replace('.000Z', '')
  const fileUrl = `https://private2.windmorbihan.com/piaf/${refDate}/PIAF_${refTime}_UTC.tiff`;
  console.debug('PIAF fileUrl: ' + fileUrl);
  let piafLayer;
  fetch(fileUrl)
    .then(response => response.arrayBuffer())
    .then(arrayBuffer => {
      window.parseGeoraster(arrayBuffer).then(georaster => {
        // console.log('georaster info: ' + JSON.stringify(georaster));
        // const min = georaster.mins[0];
        // const max = georaster.maxs[0];
        // const range = georaster.ranges[0];
        // console.debug('raster values: ' + JSON.stringify({min, max, range}));

        georaster.projection = 4326;
        piafLayer = new GeoRasterLayer({
          georaster: georaster,
          opacity: config.piafLayerOpacity,
          pixelValuesToColorFn: function(pixelValues) {
            const pixelValue = pixelValues[0];
            if (pixelValue === 0) return null;

            return piafRainColor(pixelValue);
          },
          resolution: 256
        });
        piafLayer.on('load', onPiafLayerLoad).addTo(map.instance);
        layers.bgLayers.push(piafLayer);
        renderForecastInfo('piaf', null, timestamp);
        updateTimelineDatetime(refTs.getTime() / 1000, timeline.details);
      });
    });
}

function onPiafLayerLoad(evt) {
  setTimeout(function() {
    hidePiafLayer();
    evt.target.off('load', onPiafLayerLoad);
  }, 100);
}

export function hidePiafLayer(allLayers = false) {
  const currentPiafLayers = layers.bgLayers.filter(l => l.georasters);
  if (allLayers || currentPiafLayers.length > 1) {
    const currentPiafLayerIndex = layers.bgLayers.findIndex(l => l.georasters);
    console.debug('currentPiafLayerIndex: ' + currentPiafLayerIndex.toString());
    if (currentPiafLayerIndex !== -1) {
      map.instance.removeLayer(layers.bgLayers[currentPiafLayerIndex]);
      layers.bgLayers.splice(currentPiafLayerIndex, 1);
    }
  }
}

export function hideAmp() {
  var ampZones = document.querySelectorAll(".amp_zone"), i;
  if (ampZones.length > 0) {
    for (i = 0 ; i < ampZones.length; i++){
      ampZones[i].remove();
    }
  }
  if (document.querySelector("#amp_info")) {
    document.querySelector("#amp_info").remove();
  }
}

export function hideAmpPois() {
  layers.overlay.selectAll(".amp_spot").remove()
  if (document.querySelector("#amp_info")) {
    document.querySelector("#amp_info").remove();
  }
}

export function displayAmpPois() {
  json(config.backEndUrl + "/features.json?source=amp_poi").then(function (geojsonPois) {
    renderAmpInfo();
    var ampPois = layers.overlay.select(".zoom_wrapper").selectAll(".amp_spot")
      .data(geojsonPois.features).join("g").attr("class", "amp_spot")
      .attr("transform", function (d) {
        return "translate(" + projectInMap(d.geometry.coordinates[0], d.geometry.coordinates[1]) + ")";
      })
      .on("click", ampDetails);

    ampPois.append("foreignObject").attr("width", 32).attr("height", 32)
      .append("xhtml:body")
      .html(function (d) {
        return '<div title="' + d.properties.title + '" ><img src="' + navIcons['amp-map'] + '" alt="' + d.properties.title + '"></a>';
      });
  });
}

export function displayAmp() {
  let zoneStyle = {stroke: false, fill: true, fillColor: 'rgb(224,87,247)', fillOpacity: 0.7};
  return json(config.backEndUrl + "/features.json?source=amp").then(function (geoJsonZone) {
    renderAmpInfo();
    let geoJson = L.geoJSON(geoJsonZone.features,
      {className: 'amp_zone', style: zoneStyle, pane: 'tilePane', filter: function(feature, layer) { return true; }});
    geoJson.addTo(map.instance);
    layers.bgLayers.push(geoJson);
    return geoJson;
  });
}

function ampDetails(evt, d){
  document.querySelector("#map_modal").setAttribute("data-modal", "amp");
  document.querySelector("#map_modal_title").innerHTML = d.properties.title;
  document.querySelector("#header_links").innerHTML = '<button class="modal__close" aria-label="Fermer" data-micromodal-close></button>';
  var imgMarkup = '';
  if (d.properties.img) {
    imgMarkup = "<div class='amp_photo'><img src='" + d.properties.img + "'/><p>Credit Photo : " + d.properties.credit_img1 + "</p></div>";
  }
  document.querySelector("#map_modal_content").innerHTML = "<div class='amp_modal'>" + imgMarkup +
    "<div class='amp_description'>" +
      "<h2>" + d.properties.title + "</h2>" +
      "<h3>" + d.properties.header_text + "</h3>" +
      "<h4>Lieu : " + d.properties.amp_name + "</h4>" +
      "<p>" + d.properties.description + "</p>" +
    "</div>" +
  "</div>";
  MicroModal.show('map_modal', {disableScroll: true});
}

export function webcamDetails(evt, d, onClose = null) {
  if (evt) {
    evt.preventDefault();
    evt.stopPropagation();
  }
  document.querySelector("#map_modal").setAttribute("data-modal", "webcam");
  document.querySelector("#map_modal_title").innerHTML = d.label;
  document.querySelector("#header_links").innerHTML = '<button class="modal__close" aria-label="Fermer" data-micromodal-close></button>';
  document.querySelector("#map_modal_content").innerHTML = d.content;
  const opts = onClose ? {disableScroll: true, onClose: onClose} : {disableScroll: true};
  MicroModal.show('map_modal', opts);
}

export function tideDetails(evt, d, onClose = null) {
  if (evt) {
    evt.preventDefault();
    evt.stopPropagation();
  }
  document.querySelector("#map_modal").setAttribute("data-modal", "tide");
  document.querySelector("#map_modal_title").innerHTML = d.label;
  document.querySelector("#header_links").innerHTML = '<button class="modal__close" aria-label="Fermer" data-micromodal-close></button>';
  document.querySelector("#map_modal_content").innerHTML = '<iframe id="shom_wrapper" src="javascript:void(0);"></iframe>';
  const frm = document.querySelector("#map_modal_content #shom_wrapper");
  frm.srcdoc = '<html><body style="margin: 0; padding: 0;"><script src="' + d.shomUrl + '"></script></body></html>';
  const opts = onClose ? {disableScroll: true, onClose: onClose} : {disableScroll: true};
  MicroModal.show('map_modal', opts);
}

export function displayForecastDetails(popup, lat, lng) {
  getForecastPoint(layers.forecastsLayer.forecastId, lat, lng, function (pointData) {
    if (pointData.lat) {
      map.currentFocus = pointData;
      let color = map.currentFocus.ws ? windColor(map.currentFocus.ws) : {bg: waveColor(map.currentFocus.wah), text: '#154194'};
      let value = map.currentFocus.ws ? (Math.round(map.currentFocus.ws) + 'nds') : (map.currentFocus.wah + 'm');
      let windDir = '';
      if (map.currentFocus.wind_d) {
        let dirVal = (270 - map.currentFocus.wind_d) % 360;
        dirVal = (dirVal < 10) ? ('00' + dirVal) : (dirVal < 100 ? ('0' + dirVal) : dirVal);
        windDir = ' / ' + dirVal + '°';
      }
      let waveDir = map.currentFocus.wad ? ('<img src="' + navIcons['fleche-vague'] + '" alt="direction" class="pointer_direction" style="transform: rotate(' + (map.currentFocus.wad - 180) + 'deg); transform-origin: 50% 50% 0px; display: inline;"/>') : '';
      let wavePeriod = map.currentFocus.ptp ? `  ${Math.round(map.currentFocus.ptp / 1000)}s` : '';
      popup.setContent('<p style="background-color: ' + color.bg + '; color: ' + color.text + '" class="pointer_info">' + value + (windDir || waveDir) + wavePeriod + '</p>');
    } else {
      popup.setContent(lat + ', ' + lng);
    }
  });
}

function getForecastPoint(forecastId, lat, lng, callback) {
  getAsync(config.backEndUrl + "/live_forecasts/" + forecastId + "/point?lat=" + lat + "&lng=" + lng, function (pointData) {
    callback(pointData);
  });
}

export function displayRainDetails(popup, lat, lng) {
  const currentPiafLayer = layers.bgLayers.find(l => l.georasters);
  if (currentPiafLayer) {
    const value = geoblaze.identify(currentPiafLayer.georasters[0], [lng, lat]);
    console.debug('piaf val: ' + value);
    if (value) {
      const color = piafRainColor(value), roundedVal = Math.round(value * 10) / 10.0;
      popup.setContent('<p style="background-color: ' + color + '; color: #154194" class="pointer_info">' + roundedVal + 'mm/h</p>');
    }
  }
}

export function geolocateMe() {
  map.instance.locate();
}

export function renderWindSensors(data) {
  renderWindInfo();
  const spotSensors = layers.activeSpot?.windSensors || [];
  console.debug('activeSpot: ' + layers.activeSpot?.ref + ' - spotSensors: ' + JSON.stringify(spotSensors));
  let availableSensors = data.filter(function (d) {
    return Object.keys(sensorsRefs).indexOf(d.nid.toString()) !== -1 && d.wind_pow_knot &&
      (!spotSensors.length || spotSensors.indexOf(d.nid.toString()) !== -1);
  });
  layers.overlay.select(".zoom_wrapper").selectAll(".wind_sensor").data(availableSensors, function (d) {
    return d.nid;
  }).join(
      function (enter) {
        var sensors = enter.append("g").attr("class", function(d) { return 'wind_sensor' + (tempSensorsRefs[d.nid] ? ' temp_sensor' : ''); })
          .attr("id", function (d) { return "sensor_" + d.nid; })
          .attr("transform", function (d) {
            return "translate(" + projectInMap(sensorsRefs[d.nid].lng, sensorsRefs[d.nid].lat) + ")";
          });
        sensors.append("use").attr("xlink:href", "#arrow").attr("x", "-12").attr("y", "-13")
          .attr("width", "24px").attr("height", "26px").attr("class", function (d) {
          return isValidAngle(d.nid, d.wind_dir_true) ? "" : "devent";
        })
          .attr("transform", function (d) {
            return "rotate(" + d.wind_dir_true + ", 0, 0)";
          });
        sensors.append("g").attr("class", "wind_details").attr("transform", "translate(16, -12)")
          .append("foreignObject")
          .attr("width", 60).attr("height", 30)
          .append("xhtml:body")
          .html(function (d) {
            let color = windColor(d.wind_pow_knot), colorMax = windColor(d.wind_pow_knot_max);
            return "<p>" +
              "<span style='background-color: " + color.bg + "; color: " + color.text + "'>" + d.wind_pow_knot + "</span>" +
              "<span style='background-color: " + colorMax.bg + "; color: " + colorMax.text + "'>(" + d.wind_pow_knot_max + ")</span>" +
              "</p>"
          });
        sensors.on("click", function(evt, d) {
          if (!layout.edit) {
            evt.stopPropagation();
            if (layers.activeSpot) {
              sensorDetails(evt, d);
            } else {
              router.navigate("/" + sensorsRefs[d.nid].slug);
            }

          }
        });
        sensors.on("mouseover", function (evt, d) {
          d3.select(this).raise();
          if (d3.select("#sensor_" + d.nid + " .sensor_name").size() === 0) {
            d3.select("#sensor_" + d.nid).append("text").attr("class", "sensor_name").text(sensorsRefs[d.nid].label);
          }
        }).on("mouseout", function (evt, d) {
          if (!layout.edit) {
            d3.selectAll(".sensor_name").remove();
          }
        });
        return sensors;
      },
      function (update) {
        update.select("use")
          .attr("transform", function (d) {
            return "rotate(" + d.wind_dir_true + ", 0, 0)";
          })
          .attr("class", function (d) {
            return isValidAngle(d.nid, d.wind_dir_true) ? "" : "devent";
          });
        update.select("g foreignObject body p span:first-child").transition()
          .style("background-color", function (d) {
            return windColor(d.wind_pow_knot).bg;
          })
          .style("color", function (d) {
            return windColor(d.wind_pow_knot).text;
          })
          .text(function (d) {
            return d.wind_pow_knot;
          });
        update.select("g foreignObject body p span:last-child").transition()
          .style("background-color", function (d) {
            return windColor(d.wind_pow_knot_max).bg;
          })
          .style("color", function (d) {
            return windColor(d.wind_pow_knot_max).text;
          })
          .text(function (d) {
            return '(' + d.wind_pow_knot_max + ')';
          });
        return update;
      }
    );
}

export function renderWaveSensors(data) {
  const spotSensors = layers.activeSpot?.wavesSensors || [];
  console.debug('activeSpot: ' + layers.activeSpot?.ref + ' - spotSensors: ' + JSON.stringify(spotSensors));
  let availableSensors = data.filter(function (d) {
    return Object.keys(waveSensorsRefs).indexOf(d.nid.toString()) !== -1 &&
      (!spotSensors.length || spotSensors.indexOf(d.nid.toString()) !== -1);
  });
  layers.overlay.select(".zoom_wrapper").selectAll(".wave_sensor").data(availableSensors, function (d) {
    return d.nid;
  }).join(
      function (enter) {
        let sensors = enter.append("g").attr("class", "wave_sensor").attr("id", function (d) {
          return "sensor_" + d.nid;
        }).attr("transform", function (d) {
          return "translate(" + projectInMap(waveSensorsRefs[d.nid].lng, waveSensorsRefs[d.nid].lat) + ")";
        });
        sensors.append("use").attr("xlink:href", function(d) { return d.wad ? "#wave_arrow" : "#wave_sensor_icon"; })
          .attr("x", function(d) {return d.wad ? "-18" : "-10";}).attr("y", "-19")
          .attr("width", function(d) {return d.wad ? "36px" : "24px";}).attr("height", "39px").attr("fill", "#FFFFFF")
          .attr("transform", function (d) {return d.wad ? "rotate(" + d.wad + ", 0, 0)" : "";});
        sensors.append("g").attr("class", "wave_details").attr("transform", function(d) { return d.wat ? "translate(-112, -16)" : "translate(-84, -16)"; })
          .append("foreignObject")
          .attr("width", function (d) { return d.wat ? 95 : 88; }).attr("height", 36)
          .append("xhtml:body")
          .html(function (d) {
            return "<div class='swell_data'><div class='swell_height'><span></span><span>" + d.wah + "m</span></div>" +
              (d.wap ? "<div class='swell_period'><span></span><span>" + d.wap + "s</span></div>" : '') +
              (d.wat ? "<div class='swell_temp'><span></span><span>" + d.wat + "°</span></div>" : '') + "</div>";
          });
        sensors.on("click", function (evt, d) {
          if (!layout.edit) {
            evt.stopPropagation();
            router.navigate("/" + waveSensorsRefs[d.nid].slug);
          }
        });
        sensors.on("mouseover", function (evt, d) {
          d3.select(this).raise();
          if (d3.select("#sensor_" + d.nid + " .sensor_name").size() === 0) {
            d3.select("#sensor_" + d.nid).append("text").attr("class", "sensor_name").attr("transform", "translate(-200, -16)").text(waveSensorsRefs[d.nid].label);
          }
        }).on("mouseout", function (evt, d) {
          if (!layout.edit) {
            d3.selectAll(".sensor_name").remove();
          }
          d3.select(this).lower();
        });
        sensors.lower();
        return sensors;
      },
    function (update) {
      update.select("use")
        .attr("xlink:href", function(d) { return d.wad ? "#wave_arrow" : "#wave_sensor_icon"; })
        .attr("x", function(d) {return d.wad ? "-18" : "-10";}).attr("y", "-19")
        .attr("width", function(d) {return d.wad ? "36px" : "24px";}).attr("height", "39px").attr("fill", "#FFFFFF")
        .attr("transform", function (d) {return d.wad ? "rotate(" + d.wad + ", 0, 0)" : "";});
      update.select("g foreignObject body .swell_height > span:last-child")
        .text(function (d) {return `${d.wah}m`;});
      update.select("g foreignObject body .swell_period > span:last-child")
        .text(function (d) {return `${d.wap}s`;});
      update.select("g foreignObject body .swell_temp > span:last-child")
        .text(function (d) {return `${d.wat}°`;});
        return update;
    }
  );
}

export function renderTempSensors(data) {
  const spotSensors = layers.activeSpot?.tempSensors || [];
  console.debug('activeSpot: ' + layers.activeSpot?.ref + ' - spotSensors: ' + JSON.stringify(spotSensors));

  const windActive = layers.dataLayers.indexOf('wind') !== -1;
  let availableSensors = data.filter(function (d) {
    return Object.keys(tempSensorsRefs).indexOf(d.nid.toString()) !== -1 &&
      (!spotSensors.length || spotSensors.indexOf(d.nid.toString()) !== -1);
  });
  layers.overlay.select(".zoom_wrapper").selectAll(".temp_sensor").data(availableSensors, function (d) {
    return d.nid;
  }).join(
    function (enter) {
      let sensors = enter.append("g").attr("class", "temp_sensor").attr("id", function (d) {
        return "sensor_" + d.nid;
      }).attr("transform", function (d) {
        return "translate(" + projectInMap(tempSensorsRefs[d.nid].lng, tempSensorsRefs[d.nid].lat) + ")";
      });
      sensors.append("g").attr("class", "temp_details").attr("transform", function(d) { return (windActive && sensorsRefs[d.nid]) ? "translate(80, -12)" : "translate(-16, -12)"; })
        .append("foreignObject")
        .attr("width", 30).attr("height", 36)
        .append("xhtml:body")
        .html(function (d) {
          return "<p class='" + (d.category === 'nke' ? 'water_temp' : 'air_temp') + "' style='background-color: " + (d.category === 'nke' ? '#FFF' : tempColor(d.t)) + ";'>" + d.t + "°</p>";
        });
      sensors.on("click", function (evt, d) {
        evt.stopPropagation();
        if (!layout.edit) {
          evt.stopPropagation();
          router.navigate("/" + (sensorsRefs[d.nid] ? sensorsRefs[d.nid].slug : tempSensorsRefs[d.nid].slug));
        }
      });
      sensors.on("mouseover", function (evt, d) {
        d3.select(this).raise();
        if (d3.select("#sensor_" + d.nid + " .sensor_name").size() === 0) {
          d3.select("#sensor_" + d.nid).append("text").attr("class", "sensor_name")
            .text(sensorsRefs[d.nid] ? sensorsRefs[d.nid].label : tempSensorsRefs[d.nid].label).attr("text-anchor", "middle");
        }
      }).on("mouseout", function (evt, d) {
        if (!layout.edit) {
          d3.selectAll(".sensor_name").remove();
        }
        d3.select(this).lower();
      });
      sensors.lower();
      return sensors;
    },
    function (update) {
      update.append("g").attr("class", "temp_details").attr("transform", function(d) { return (windActive && sensorsRefs[d.nid]) ? "translate(80, -12)" : "translate(-16, -12)"; })
        .append("foreignObject")
        .attr("width", 30).attr("height", 36)
        .append("xhtml:body")
        .html(function (d) {
          return "<p class='" + (d.category === 'nke' ? 'water_temp' : 'air_temp') + "' style='background-color: " + (d.category === 'nke' ? '#FFF' : tempColor(d.t)) + ";'>" + d.t + "°</p>";
        });
      update.select("g foreignObject body .air_temp").attr("style", function(d) {return `background-color: ${tempColor(d.t)};`;}).text(function(d) {return `${d.t}°`;});

      return update;
    }
  );
}

export function updatePositions() {
  layers.overlay.selectAll(".wind_sensor")
    .attr("transform", function (d) {
      return "translate(" + projectInMap(sensorsRefs[d.nid].lng, sensorsRefs[d.nid].lat) + ")";
    });
  layers.overlay.selectAll(".wave_sensor")
    .attr("transform", function (d) {
      return "translate(" + projectInMap(waveSensorsRefs[d.nid].lng, waveSensorsRefs[d.nid].lat) + ")";
    });
  layers.overlay.selectAll(".temp_sensor:not(.wind_sensor)")
    .attr("transform", function (d) {
      return "translate(" + projectInMap(tempSensorsRefs[d.nid].lng, tempSensorsRefs[d.nid].lat) + ")";
    });
  layers.overlay.selectAll(".weather_poi")
    .attr("transform", function (d) {
      return "translate(" + projectInMap(d.lng, d.lat) + ")";
    });
  layers.overlay.selectAll(".amp_spot")
    .attr("transform", function (d) {
      return "translate(" + projectInMap(d.geometry.coordinates[0], d.geometry.coordinates[1]) + ")";
    });
  layers.overlay.selectAll(".webcam_spot, .water_temp_sensor, .tide_spot")
    .attr("transform", function (d) {
      return "translate(" + projectInMap(d.lng, d.lat) + ")";
    });
}

export function toggleLayer(btn, layerRef) {
  var mapContainer = document.querySelector("#map_container");
  if (layers.dataLayers.indexOf(layerRef) !== -1) {
    layers.dataLayers.splice(layers.dataLayers.indexOf(layerRef), 1);
    removeLayer(layerRef);
    if (layerRef === "wind") {
      var layerInfoContainer = document.querySelector("#wind_info");
      if (layerInfoContainer) {
        layerInfoContainer.remove();
      }
    }
    if (layerRef === "wave_fc" && map.activeMap === 'wm') {
      displayCoastLine(true)
    }
    mapContainer.classList.remove(layerRef);
  } else {
    addLayer(layerRef);
    layers.dataLayers.push(layerRef);
    mapContainer.classList.add(layerRef);
  }
  let btnElt = btn.classList.contains('layer_preview') ? document.querySelector(`#map_layers #${layerRef}`) : btn;
  const sideBtn = document.querySelector(`#${layerRef}_side`);

  if (layerRef === "legend") {
    btnElt = document.querySelector("#legend");
  }
  if (btnElt) {
    btnElt.classList.toggle('active');
  }
  mapContainer.classList.remove("hide_inactive");
  if (sideBtn) {
    sideBtn.classList.toggle('active');
  }
  logPageView(router.lastResolved(), '/map/' + layerRef, layerRef);
}

function isValidAngle(sensorId, windDir) {
  let validAngle = sensorsRefs[sensorId].validAngle, dir = parseInt(windDir);
  if (validAngle && validAngle.filter(v => v).length > 0) {
    let fromAngle, toAngle;
    if (validAngle.length === 3) {
      fromAngle = validAngle[0];
      toAngle = validAngle[2];
    } else {
      fromAngle = validAngle[0];
      toAngle = validAngle[1];
    }
    if (fromAngle > toAngle) {
      return (dir >= fromAngle && dir <= 360) || (dir >= 0 && dir <= toAngle);
    } else {
      return dir >= fromAngle && dir <= toAngle;
    }
  }
  return true;
}

export function addLayer(layerRef) {
  console.debug('Adding layer ' + layerRef);
  switch (layerRef) {
    case 'wind_fc':
      displayWindForecasts(timeline.timelineActiveStep ? d3.select(timeline.timelineActiveStep).datum()[0] : null);
      break;
    case 'waves_fc':
      displayWavesForecasts(timeline.timelineActiveStep ? d3.select(timeline.timelineActiveStep).datum()[0] : null);
      break;
    case 'weather_fc':
      displayPOIs(timeline.timelineActiveStep ? d3.select(timeline.timelineActiveStep).datum()[0] : null);
      break;
    case 'wind':
      displayWindMeasures();
      break;
    case 'waves':
      displayWaveMeasures();
      break;
    case 'webcam':
      displayWebcams();
      break;
    case 'temp':
      displayTemperatures();
      break;
    case 'temp_water':
      displayWaterTemp();
      break;
    case 'tide_fc':
      displayTide();
      break;
    case 'amp':
      displayAmp();
      break;
    case 'amp_poi':
      displayAmpPois();
      break;
    case 'rain_fc':
      displayPiafLayer(timeline.timelineActiveStep ? d3.select(timeline.timelineActiveStep).datum()[0] : null);
      break;
    default:
      console.debug('Unsupported layer ref : ' + layerRef);
  }
  if (layout.edit) {
    setTimeout(function() {
      const layerElements = document.querySelectorAll("g.zoom_wrapper > g");
      console.debug(`adding listener to ${layerElements.length} elements`);
      for (const elt of layerElements) {
        if (!elt.getAttribute('data-edit')) {
          elt.setAttribute('data-edit', 'true');
          const nestedLink = elt.querySelector("a");
          if (nestedLink) {
            nestedLink.setAttribute("data-href", nestedLink.getAttribute('href'));
            nestedLink.removeAttribute('href');
          }
          elt.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            const layerItem = e.target.closest("g[data-edit]");
            if (layerItem) {
              layerItem.classList.toggle('edit_pinned');
            }
            if (layerItem.classList.contains('edit_pinned') && layerItem.classList.contains('weather_poi') &&
              document.querySelectorAll(".weather_poi.edit_pinned").length === 1) {
              layerItem.setAttribute("data-main-poi", "true");
            }
          });
        }
      }
    }, 1000);
  }
}

export function removeLayer(layerRef, displayMap = true) {
  console.debug('Removing layer ' + layerRef);
  switch (layerRef) {
    case 'wind_fc':
      hideWindForecasts();
      if (displayMap) {
        displayMapRef(map.activeMap);
      }
      break;
    case 'waves_fc':
      hideWavesForecasts();
      if (displayMap) {
        displayMapRef(map.activeMap);
      }
      break;
    case 'weather_fc':
      hidePOIs();
      break;
    case 'wind':
      hideWindMeasures();
      break;
    case 'waves':
      hideWaveMeasures();
      break;
    case 'webcam':
      hideWebcams();
      break;
    case 'temp':
      hideTemperatures();
      break;
    case 'temp_water':
      hideWaterTemperatures();
      break;
    case 'tide_fc':
      hideTide();
      break;
    case 'amp':
      hideAmp();
      break;
    case 'amp_poi':
      hideAmpPois();
      break;
    case 'rain_fc':
      hidePiafLayer(true);
      break;
    default:
      console.debug('Unsupported layer ref : ' + layerRef);
  }
}
